// Markdown body for the post. Metadata lives in src/blog/posts.json.
const body = `
The setting is the part that fails. Stones are hard and last centuries; the metal holding them is soft, thin and worked every day against door handles and pockets. So when you are judging an antique ring, the setting tells you both what period it belongs to and what it is going to cost you.

## The settings you will actually meet

**Claw, also called prong.** Small fingers of metal bent over the stone. Lets in the most light, which is why it dominates for diamonds. Also the most exposed: claw tips wear thin, bend and snap.

**Bezel, also called rub-over.** A collar of metal burnished around the whole stone. The most protective setting there is, and the reason so many antique rings survive intact. It shows less of the stone and can make it look slightly smaller.

**Gypsy, also called flush or star.** The stone is sunk into the body of the ring so its table sits level with the metal. Extremely durable, common on Victorian men's rings, and often finished with engraved rays radiating from the stone.

**Cluster.** A central stone ringed by smaller ones, so the whole reads as one larger stone. Victorian and Edwardian favourites. Check every small stone: clusters lose them quietly.

**Illusion.** The stone sits in a worked, faceted white metal plate designed to make it look bigger. Invented in the mid nineteenth century, most common from the early twentieth century through the 1950s, and easy to mistake for a larger stone at a glance.

**Channel.** Stones sit in a groove between two walls of metal, touching each other with no metal between. This is where you find [calibre cut stones](/blog/art-deco-vs-art-nouveau-jewelry) in Art Deco work.

**Pave.** A surface covered in small stones held by tiny beads of metal raised from the surface itself.

**Tension.** The stone is held by the spring of the metal alone. Modern only, and it cannot be resized.

## Which setting belongs to which period

| Setting | Most associated with |
| --- | --- |
| Closed back, foiled | Georgian to early Victorian, before about 1860 |
| Gypsy and star | Victorian, especially men's rings |
| Cluster | Victorian and Edwardian |
| Millegrain edged bezel | Edwardian, roughly 1901 to 1915 |
| Channel with calibre cut stones | Art Deco, 1920 to 1935 |
| Illusion | early 1900s to 1950s |
| Tension | Late twentieth century onwards |

A closed back is worth stopping at. Before diamond cutting was good enough for stones to perform on their own, jewellers backed them with coloured foil and sealed the setting. Water gets under the foil and ruins it permanently, which is why our [cleaning guide](/blog/how-to-clean-antique-jewelry) says never to put those rings in water. The [era by era guide](/blog/how-to-date-antique-jewelry-by-era) has more dating evidence.

## What to inspect before you buy

**Claw tips.** Look from the side, not from above. A healthy claw has a visible pad of metal folded over the stone. A worn one is a thin spike, or has a flattened, polished-away top. Re-tipping is routine work and not expensive, but it should be priced in.

**Movement.** Hold the ring close to your ear and tap the band gently with a fingernail. A loose stone ticks. This is the single most useful ten-second check there is.

**The bottom of the shank.** A century of wear thins it. Slightly thin is normal and part of the character; paper thin means it needs replacing, which costs real money and removes originality.

**Millegrain.** On Edwardian and Deco pieces, that beaded edge should still read as distinct beads. Flat, smooth millegrain means heavy wear or over-polishing.

**Solder colour.** A patch of noticeably different colour usually marks a past repair or a resize.

**Under the stone.** Ask for a photograph looking into the back of the setting. Dirt hides there, and so do chips.

## Settings and resizing

This is where sizing questions get answered, and it is worth asking before you fall in love with a ring.

- **Plain shank, stone at the top**: usually resizable, two sizes either way.
- **Stones running round the shoulders**: limited, because cutting into a stone-set section is not possible.
- **Eternity band**: not resizable. Stones all the way round leave nothing to cut.
- **Hallmarked shank**: resizing may remove the marks, and with British assay marks that means losing the date letter.
- **Carved or engraved shank**: the pattern is interrupted where new metal goes in.
- **Closed back or foiled**: soldering heat risks the foil and sometimes the stone.
- **Tension**: no.

Our [ring size guide](/blog/how-to-measure-your-ring-size-at-home) covers how to measure properly and the conversions between systems.

## Settings and durability, if you wear rings hard

If a ring is going to be worn every day, on a working hand:

- **Bezel and gypsy** are the survivors. They protect the stone's girdle, which is where chips start.
- **Low claw settings** with four or six sturdy claws are the practical compromise.
- **High, delicate claw settings** and **pave** need more care and more frequent checks.
- **Soft stones** in exposed settings are a bad combination whatever the metal.

## What a repair costs you beyond money

Re-tipping claws, replacing a shank, resetting a stone: all are normal maintenance on a hundred-year-old ring. Each one also removes a small amount of originality, and originality is what collectors pay for. A ring that has had its shank replaced and its claws rebuilt is a good ring to wear and a weaker one to resell.

That is not an argument against repair. It is an argument for asking what has already been done, and factoring it into the price. Our [resale value guide](/blog/how-much-is-my-jewelry-worth) explains how condition and originality move the number.

## Questions worth asking a seller

1. What type of setting is it, and is it original to the piece?
2. Have any claws been re-tipped, or has the shank been replaced?
3. Does the stone move if you tap it?
4. Can it be resized, and has it been resized before?
5. Can I see a photograph looking into the back of the setting?

On Nextmine the buyer's payment is held until the ring has arrived and been confirmed, so a loose stone or an undisclosed repair is something you can act on. [See the rings listed](/s), or read [how payments work](/how-payments-work).

Millegrain and pierced galleries are covered in more depth in the Hallmark Society's [Edwardian guide](https://hallmarksociety.com/edwardian-jewelry/).
`;

export default body;
